import { useEffect, useState } from 'react';
import { Save, RefreshCw, Target, Settings, Plus, X, ShieldCheck } from 'lucide-react';
import Card from '@/components/ui/Card';
import Tooltip from '@/components/ui/Tooltip';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ModelRoutingEntry {
  task_type: string;
  tier: string;
  model?: string;
}

interface PricingRules {
  min_margin_percent: number;
  default_markup_percent: number;
  max_markup_percent: number;
  auto_apply: boolean;
}

interface EscalationRules {
  max_retries: number;
  price_spike_threshold_percent: number;
  supplier_response_timeout_hours: number;
}

interface CategoryEntry {
  name: string;
  priority: 'high' | 'medium' | 'low';
}

interface AgentStrategy {
  product_preferences: {
    categories: CategoryEntry[];
    keywords: string[];
    exclude_keywords: string[];
  };
  supplier_preferences: {
    listing_model: 'free_only' | 'paid_ok' | 'no_preference';
    business_model: ('dropship' | 'wholesale')[];
    dropship_scenarios: ('true_dropship' | 'wholesale_dropship' | 'consignment')[];
    payment_terms: ('prepaid' | 'net_15' | 'net_30' | 'net_60' | 'no_preference')[];
    min_confidence_score: number;
  };
  shipping_preferences: {
    preferred_regions: string[];
    domestic_only: boolean;
    max_lead_time_days: number;
    preferred_speed: 'economy' | 'standard' | 'express' | 'no_preference';
  };
}

interface ApprovalLevels {
  global_preset: 'full_manual' | 'suggest_only' | 'auto_with_review' | 'fully_autonomous';
  overrides: {
    auto_publish_products: {
      enabled: boolean;
      conditions: { max_price: number | null; require_matching_rule: boolean };
    };
    auto_adjust_prices: {
      enabled: boolean;
      conditions: { max_change_percent: number | null; only_decreases: boolean };
    };
    auto_place_orders: {
      enabled: boolean;
      conditions: { max_order_total: number | null; trusted_suppliers_only: boolean };
    };
    auto_promote_prospects: { enabled: boolean };
    auto_chain_research: { enabled: boolean };
    auto_send_supplier_emails: {
      enabled: boolean;
      conditions: { templates_only: boolean; max_per_day: number };
    };
  };
}

interface AgentConfig {
  model_routing: Record<string, { tier: string; model?: string }>;
  escalation_rules: EscalationRules;
  pricing_rules: PricingRules;
  agent_strategy: AgentStrategy;
  approval_levels: ApprovalLevels;
}

// ---------------------------------------------------------------------------
// Defaults & constants
// ---------------------------------------------------------------------------

const DEFAULT_STRATEGY: AgentStrategy = {
  product_preferences: { categories: [], keywords: [], exclude_keywords: [] },
  supplier_preferences: {
    listing_model: 'no_preference',
    business_model: ['dropship'],
    dropship_scenarios: ['true_dropship'],
    payment_terms: ['no_preference'],
    min_confidence_score: 50,
  },
  shipping_preferences: {
    preferred_regions: ['US'],
    domestic_only: false,
    max_lead_time_days: 14,
    preferred_speed: 'no_preference',
  },
};

const DEFAULT_APPROVALS: ApprovalLevels = {
  global_preset: 'full_manual',
  overrides: {
    auto_publish_products: { enabled: false, conditions: { max_price: null, require_matching_rule: false } },
    auto_adjust_prices: { enabled: false, conditions: { max_change_percent: null, only_decreases: false } },
    auto_place_orders: { enabled: false, conditions: { max_order_total: null, trusted_suppliers_only: false } },
    auto_promote_prospects: { enabled: false },
    auto_chain_research: { enabled: false },
    auto_send_supplier_emails: { enabled: false, conditions: { templates_only: true, max_per_day: 10 } },
  },
};

const PRESET_OVERRIDES: Record<string, ApprovalLevels['overrides']> = {
  full_manual: {
    auto_publish_products: { enabled: false, conditions: { max_price: null, require_matching_rule: false } },
    auto_adjust_prices: { enabled: false, conditions: { max_change_percent: null, only_decreases: false } },
    auto_place_orders: { enabled: false, conditions: { max_order_total: null, trusted_suppliers_only: false } },
    auto_promote_prospects: { enabled: false },
    auto_chain_research: { enabled: false },
    auto_send_supplier_emails: { enabled: false, conditions: { templates_only: true, max_per_day: 10 } },
  },
  suggest_only: {
    auto_publish_products: { enabled: false, conditions: { max_price: null, require_matching_rule: false } },
    auto_adjust_prices: { enabled: false, conditions: { max_change_percent: null, only_decreases: false } },
    auto_place_orders: { enabled: false, conditions: { max_order_total: null, trusted_suppliers_only: false } },
    auto_promote_prospects: { enabled: false },
    auto_chain_research: { enabled: true },
    auto_send_supplier_emails: { enabled: false, conditions: { templates_only: true, max_per_day: 10 } },
  },
  auto_with_review: {
    auto_publish_products: { enabled: true, conditions: { max_price: null, require_matching_rule: true } },
    auto_adjust_prices: { enabled: true, conditions: { max_change_percent: 10, only_decreases: false } },
    auto_place_orders: { enabled: true, conditions: { max_order_total: 100, trusted_suppliers_only: true } },
    auto_promote_prospects: { enabled: true },
    auto_chain_research: { enabled: true },
    auto_send_supplier_emails: { enabled: true, conditions: { templates_only: true, max_per_day: 10 } },
  },
  fully_autonomous: {
    auto_publish_products: { enabled: true, conditions: { max_price: null, require_matching_rule: false } },
    auto_adjust_prices: { enabled: true, conditions: { max_change_percent: null, only_decreases: false } },
    auto_place_orders: { enabled: true, conditions: { max_order_total: null, trusted_suppliers_only: false } },
    auto_promote_prospects: { enabled: true },
    auto_chain_research: { enabled: true },
    auto_send_supplier_emails: { enabled: true, conditions: { templates_only: false, max_per_day: 50 } },
  },
};

const TIER_OPTIONS = ['cheap', 'standard', 'powerful'];
const TIER_COLORS: Record<string, string> = {
  cheap: 'bg-green-100 text-green-800',
  standard: 'bg-blue-100 text-blue-800',
  powerful: 'bg-purple-100 text-purple-800',
};

const PRIORITY_OPTIONS: CategoryEntry['priority'][] = ['high', 'medium', 'low'];
const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-gray-100 text-gray-700',
};

const PAYMENT_TERM_LABELS: Record<string, string> = {
  prepaid: 'Prepaid',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_60: 'Net 60',
  no_preference: 'No preference',
};

const SPEED_OPTIONS = [
  { value: 'economy', label: 'Economy' },
  { value: 'standard', label: 'Standard' },
  { value: 'express', label: 'Express' },
  { value: 'no_preference', label: 'No preference' },
];

const PRESET_OPTIONS = [
  { value: 'full_manual', label: 'Full Manual', desc: 'Agent suggests, you decide everything' },
  { value: 'suggest_only', label: 'Suggest Only', desc: 'Agent researches autonomously, actions need approval' },
  { value: 'auto_with_review', label: 'Auto + Review', desc: 'Auto-executes within limits, escalates the rest' },
  { value: 'fully_autonomous', label: 'Fully Autonomous', desc: 'Agent handles everything independently' },
];

const PRESET_ACTION_ROWS: Array<{ key: keyof ApprovalLevels['overrides']; label: string }> = [
  { key: 'auto_publish_products', label: 'Publish products' },
  { key: 'auto_adjust_prices', label: 'Adjust prices' },
  { key: 'auto_place_orders', label: 'Place supplier orders' },
  { key: 'auto_promote_prospects', label: 'Promote prospects' },
  { key: 'auto_chain_research', label: 'Chain research after discovery' },
  { key: 'auto_send_supplier_emails', label: 'Send supplier emails' },
];

type Tab = 'strategy' | 'technical';

const TABS: { key: Tab; label: string; icon: typeof Target }[] = [
  { key: 'strategy', label: 'Strategy', icon: Target },
  { key: 'technical', label: 'Technical', icon: Settings },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AgentConfig() {
  const [activeTab, setActiveTab] = useState<Tab>('strategy');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Strategy state
  const [strategy, setStrategy] = useState<AgentStrategy>(DEFAULT_STRATEGY);
  const [approvals, setApprovals] = useState<ApprovalLevels>(DEFAULT_APPROVALS);
  const [newCategory, setNewCategory] = useState('');

  // Technical state
  const [routingRows, setRoutingRows] = useState<ModelRoutingEntry[]>([]);
  const [pricing, setPricing] = useState<PricingRules>({
    min_margin_percent: 15,
    default_markup_percent: 30,
    max_markup_percent: 80,
    auto_apply: true,
  });
  const [escalation, setEscalation] = useState<EscalationRules>({
    max_retries: 3,
    price_spike_threshold_percent: 20,
    supplier_response_timeout_hours: 24,
  });

  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const res = await api.get<{ config: AgentConfig }>('/admin/agent/config');
        if (res.config.model_routing) {
          setRoutingRows(
            Object.entries(res.config.model_routing).map(([task_type, v]) => ({
              task_type,
              tier: v.tier,
              model: v.model,
            }))
          );
        }
        if (res.config.pricing_rules) setPricing(res.config.pricing_rules);
        if (res.config.escalation_rules) setEscalation(res.config.escalation_rules);
        if (res.config.agent_strategy) setStrategy(res.config.agent_strategy);
        if (res.config.approval_levels) setApprovals(res.config.approval_levels);
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to load config', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveAll = async () => {
    try {
      setIsSaving(true);
      const model_routing: Record<string, { tier: string; model?: string }> = {};
      for (const row of routingRows) {
        model_routing[row.task_type] = { tier: row.tier, ...(row.model ? { model: row.model } : {}) };
      }
      await api.put('/admin/agent/config', {
        model_routing,
        pricing_rules: pricing,
        escalation_rules: escalation,
        agent_strategy: strategy,
        approval_levels: approvals,
      });
      addToast('Configuration saved', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to save config', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Strategy helpers ──

  const addCategory = () => {
    const name = newCategory.trim();
    if (!name) return;
    if (strategy.product_preferences.categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return;
    setStrategy((s) => ({
      ...s,
      product_preferences: {
        ...s.product_preferences,
        categories: [...s.product_preferences.categories, { name, priority: 'medium' }],
      },
    }));
    setNewCategory('');
  };

  const removeCategory = (idx: number) => {
    setStrategy((s) => ({
      ...s,
      product_preferences: {
        ...s.product_preferences,
        categories: s.product_preferences.categories.filter((_, i) => i !== idx),
      },
    }));
  };

  const setCategoryPriority = (idx: number, priority: CategoryEntry['priority']) => {
    setStrategy((s) => ({
      ...s,
      product_preferences: {
        ...s.product_preferences,
        categories: s.product_preferences.categories.map((c, i) => (i === idx ? { ...c, priority } : c)),
      },
    }));
  };

  const applyPreset = (preset: ApprovalLevels['global_preset']) => {
    setApprovals({
      global_preset: preset,
      overrides: JSON.parse(JSON.stringify(PRESET_OVERRIDES[preset])),
    });
  };

  const updateOverride = <K extends keyof ApprovalLevels['overrides']>(
    key: K,
    value: ApprovalLevels['overrides'][K]
  ) => {
    setApprovals((prev) => ({
      ...prev,
      global_preset: 'full_manual', // will be recalculated
      overrides: { ...prev.overrides, [key]: value },
    }));
  };

  const toggleBusinessModel = (model: 'dropship' | 'wholesale') => {
    setStrategy((s) => {
      const current = s.supplier_preferences.business_model;
      const next = current.includes(model) ? current.filter((m) => m !== model) : [...current, model];
      if (next.length === 0) return s; // must have at least one
      return { ...s, supplier_preferences: { ...s.supplier_preferences, business_model: next } };
    });
  };

  const togglePaymentTerm = (term: string) => {
    setStrategy((s) => {
      const current = s.supplier_preferences.payment_terms;
      const next = current.includes(term as any) ? current.filter((t) => t !== term) : [...current, term as any];
      if (next.length === 0) return s;
      return { ...s, supplier_preferences: { ...s.supplier_preferences, payment_terms: next } };
    });
  };

  const addRegion = (region: string) => {
    const r = region.trim().toUpperCase();
    if (!r || strategy.shipping_preferences.preferred_regions.includes(r)) return;
    setStrategy((s) => ({
      ...s,
      shipping_preferences: { ...s.shipping_preferences, preferred_regions: [...s.shipping_preferences.preferred_regions, r] },
    }));
  };

  const removeRegion = (idx: number) => {
    setStrategy((s) => ({
      ...s,
      shipping_preferences: {
        ...s.shipping_preferences,
        preferred_regions: s.shipping_preferences.preferred_regions.filter((_, i) => i !== idx),
      },
    }));
  };

  // ── Technical helpers ──

  const updateRoutingTier = (index: number, tier: string) => {
    setRoutingRows((prev) => prev.map((r, i) => (i === index ? { ...r, tier } : r)));
  };

  // ── Render ──

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Agent Configuration</h1>
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agent Configuration</h1>
          <p className="text-gray-600 mt-1">Define what the agent does and how autonomous it should be.</p>
        </div>
        <button
          onClick={handleSaveAll}
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save All
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Agent config tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === tab.key ? 'page' : undefined}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ═══ Tab: Strategy ═══ */}
      {activeTab === 'strategy' && (
        <div className="space-y-6">
          {/* Product Preferences */}
          <Card title="Product Preferences" padding="md">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Categories</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                    placeholder="e.g. products, supplies, tools..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={addCategory}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                {strategy.product_preferences.categories.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {strategy.product_preferences.categories.map((cat, i) => (
                      <div key={i} className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
                        <span className="text-sm text-gray-800">{cat.name}</span>
                        <select
                          value={cat.priority}
                          onChange={(e) => setCategoryPriority(i, e.target.value as CategoryEntry['priority'])}
                          className={`text-xs font-medium rounded-full px-1.5 py-0.5 border-0 ${PRIORITY_COLORS[cat.priority]}`}
                        >
                          {PRIORITY_OPTIONS.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                        <button onClick={() => removeCategory(i)} className="text-gray-400 hover:text-red-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No categories added. The agent will search for any products.</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search Keywords</label>
                  <input
                    type="text"
                    value={strategy.product_preferences.keywords.join(', ')}
                    onChange={(e) => setStrategy((s) => ({
                      ...s,
                      product_preferences: {
                        ...s.product_preferences,
                        keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                      },
                    }))}
                    placeholder="premium, certified, specialty..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Comma-separated. Added to supplier search queries.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Exclude Keywords</label>
                  <input
                    type="text"
                    value={strategy.product_preferences.exclude_keywords.join(', ')}
                    onChange={(e) => setStrategy((s) => ({
                      ...s,
                      product_preferences: {
                        ...s.product_preferences,
                        exclude_keywords: e.target.value.split(',').map((k) => k.trim()).filter(Boolean),
                      },
                    }))}
                    placeholder="synthetic, chemical..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Comma-separated. Agent will avoid these.</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Supplier Preferences */}
          <Card title="Supplier Preferences" padding="md">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Listing Model</label>
                <div className="flex gap-3">
                  {[
                    { value: 'free_only', label: 'Free to list only', desc: 'No upfront costs to list products' },
                    { value: 'paid_ok', label: 'Paid OK', desc: 'Willing to pay for listing/membership' },
                    { value: 'no_preference', label: 'No preference', desc: 'Either is fine' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                        strategy.supplier_preferences.listing_model === opt.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="listing_model"
                        value={opt.value}
                        checked={strategy.supplier_preferences.listing_model === opt.value}
                        onChange={(e) => setStrategy((s) => ({
                          ...s,
                          supplier_preferences: { ...s.supplier_preferences, listing_model: e.target.value as any },
                        }))}
                        className="sr-only"
                      />
                      <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Model</label>
                  <div className="space-y-2">
                    {(['dropship', 'wholesale'] as const).map((model) => (
                      <label key={model} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={strategy.supplier_preferences.business_model.includes(model)}
                          onChange={() => toggleBusinessModel(model)}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700 capitalize">{model}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dropship Scenarios
                    <Tooltip content="Which payment arrangements to consider. True dropship = pay only when they ship (no upfront costs)." />
                  </label>
                  <div className="space-y-2">
                    {([
                      { value: 'true_dropship', label: 'True Dropship', desc: 'Pay per ship, no upfront costs' },
                      { value: 'wholesale_dropship', label: 'Wholesale Dropship', desc: 'Buy inventory, supplier ships' },
                      { value: 'consignment', label: 'Consignment', desc: 'Supplier retains ownership until sold' },
                    ] as const).map((scenario) => (
                      <label key={scenario.value} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={(strategy.supplier_preferences.dropship_scenarios || ['true_dropship']).includes(scenario.value)}
                          onChange={() => {
                            setStrategy((s) => {
                              const current = s.supplier_preferences.dropship_scenarios || ['true_dropship'];
                              const next = current.includes(scenario.value)
                                ? current.filter((v) => v !== scenario.value)
                                : [...current, scenario.value];
                              return { ...s, supplier_preferences: { ...s.supplier_preferences, dropship_scenarios: next.length > 0 ? next : ['true_dropship'] } };
                            });
                          }}
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div>
                          <span className="text-sm text-gray-700">{scenario.label}</span>
                          <div className="text-xs text-gray-500">{scenario.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                  <div className="space-y-2">
                    {Object.entries(PAYMENT_TERM_LABELS).map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={strategy.supplier_preferences.payment_terms.includes(value as any)}
                          onChange={() => togglePaymentTerm(value)}
                          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                  Min Confidence Score
                  <Tooltip content="Minimum research confidence (0-100) required before the agent considers a prospect viable. Higher = stricter." />
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={strategy.supplier_preferences.min_confidence_score}
                    onChange={(e) => setStrategy((s) => ({
                      ...s,
                      supplier_preferences: { ...s.supplier_preferences, min_confidence_score: Number(e.target.value) },
                    }))}
                    className="flex-1 accent-green-600"
                  />
                  <span className="text-sm font-mono text-gray-700 w-8 text-right">
                    {strategy.supplier_preferences.min_confidence_score}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Shipping Preferences */}
          <Card title="Shipping Preferences" padding="md">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Regions</label>
                <div className="flex gap-2 mb-2">
                  <RegionInput onAdd={addRegion} />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {strategy.shipping_preferences.preferred_regions.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
                      {r}
                      <button onClick={() => removeRegion(i)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {strategy.shipping_preferences.preferred_regions.length === 0 && (
                    <p className="text-xs text-gray-400">No regions specified — agent will consider any region.</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={strategy.shipping_preferences.domestic_only}
                      onChange={(e) => setStrategy((s) => ({
                        ...s,
                        shipping_preferences: { ...s.shipping_preferences, domestic_only: e.target.checked },
                      }))}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Domestic only</span>
                  </label>
                  <p className="text-xs text-gray-400 mt-1 ml-6">Only consider suppliers that ship domestically</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Lead Time (days)</label>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={strategy.shipping_preferences.max_lead_time_days}
                    onChange={(e) => setStrategy((s) => ({
                      ...s,
                      shipping_preferences: { ...s.shipping_preferences, max_lead_time_days: Number(e.target.value) || 14 },
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Speed</label>
                  <select
                    value={strategy.shipping_preferences.preferred_speed}
                    onChange={(e) => setStrategy((s) => ({
                      ...s,
                      shipping_preferences: { ...s.shipping_preferences, preferred_speed: e.target.value as any },
                    }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {SPEED_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </Card>

          {/* Autonomy & Approval Levels */}
          <Card title="Autonomy & Approval Levels" padding="md">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Autonomy Preset</label>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm" data-testid="preset-matrix">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 w-56">Action</th>
                        {PRESET_OPTIONS.map((opt) => {
                          const selected = approvals.global_preset === opt.value;
                          return (
                            <th
                              key={opt.value}
                              className={cn(
                                'px-3 py-2 text-center text-xs cursor-pointer transition-colors',
                                selected ? 'bg-green-100 text-green-900' : 'text-gray-600 hover:bg-gray-100'
                              )}
                              onClick={() => applyPreset(opt.value as ApprovalLevels['global_preset'])}
                            >
                              <div className="font-semibold">{opt.label}</div>
                              <div className="font-normal text-[10px] text-gray-500 mt-0.5 leading-tight">{opt.desc}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {PRESET_ACTION_ROWS.map((row) => (
                        <tr key={row.key} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-700">{row.label}</td>
                          {PRESET_OPTIONS.map((opt) => {
                            const enabled = PRESET_OVERRIDES[opt.value][row.key].enabled;
                            const selected = approvals.global_preset === opt.value;
                            return (
                              <td
                                key={opt.value}
                                className={cn('px-3 py-2 text-center text-base', selected && 'bg-green-50')}
                              >
                                <span
                                  className={cn(enabled ? 'text-green-600' : 'text-gray-300')}
                                  title={enabled ? 'Auto-executes' : 'Requires manual approval'}
                                >
                                  {enabled ? '✓' : '✗'}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Click a preset header to apply. ✓ = agent acts autonomously, ✗ = requires your approval. Use Fine-tune below to override individual actions.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Fine-tune</span>
                  <span className="text-xs text-gray-400">Override individual actions</span>
                </div>

                <div className="space-y-4">
                  {/* Auto-publish products */}
                  <OverrideToggle
                    label="Auto-publish products"
                    description="Publish product drafts without manual approval"
                    enabled={approvals.overrides.auto_publish_products.enabled}
                    onToggle={(v) => updateOverride('auto_publish_products', { ...approvals.overrides.auto_publish_products, enabled: v })}
                  >
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max price ($)</label>
                        <input
                          type="number"
                          min={0}
                          value={approvals.overrides.auto_publish_products.conditions.max_price ?? ''}
                          onChange={(e) => updateOverride('auto_publish_products', {
                            ...approvals.overrides.auto_publish_products,
                            conditions: { ...approvals.overrides.auto_publish_products.conditions, max_price: e.target.value ? Number(e.target.value) : null },
                          })}
                          placeholder="No limit"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      <label className="flex items-center gap-2 self-end pb-1">
                        <input
                          type="checkbox"
                          checked={approvals.overrides.auto_publish_products.conditions.require_matching_rule}
                          onChange={(e) => updateOverride('auto_publish_products', {
                            ...approvals.overrides.auto_publish_products,
                            conditions: { ...approvals.overrides.auto_publish_products.conditions, require_matching_rule: e.target.checked },
                          })}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-xs text-gray-600">Require matching publish rule</span>
                      </label>
                    </div>
                  </OverrideToggle>

                  {/* Auto-adjust prices */}
                  <OverrideToggle
                    label="Auto-adjust prices"
                    description="Apply price changes without manual approval"
                    enabled={approvals.overrides.auto_adjust_prices.enabled}
                    onToggle={(v) => updateOverride('auto_adjust_prices', { ...approvals.overrides.auto_adjust_prices, enabled: v })}
                  >
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max change (%)</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={approvals.overrides.auto_adjust_prices.conditions.max_change_percent ?? ''}
                          onChange={(e) => updateOverride('auto_adjust_prices', {
                            ...approvals.overrides.auto_adjust_prices,
                            conditions: { ...approvals.overrides.auto_adjust_prices.conditions, max_change_percent: e.target.value ? Number(e.target.value) : null },
                          })}
                          placeholder="No limit"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      <label className="flex items-center gap-2 self-end pb-1">
                        <input
                          type="checkbox"
                          checked={approvals.overrides.auto_adjust_prices.conditions.only_decreases}
                          onChange={(e) => updateOverride('auto_adjust_prices', {
                            ...approvals.overrides.auto_adjust_prices,
                            conditions: { ...approvals.overrides.auto_adjust_prices.conditions, only_decreases: e.target.checked },
                          })}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-xs text-gray-600">Only price decreases</span>
                      </label>
                    </div>
                  </OverrideToggle>

                  {/* Auto-place orders */}
                  <OverrideToggle
                    label="Auto-place supplier orders"
                    description="Place orders with suppliers without manual approval"
                    enabled={approvals.overrides.auto_place_orders.enabled}
                    onToggle={(v) => updateOverride('auto_place_orders', { ...approvals.overrides.auto_place_orders, enabled: v })}
                  >
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max order total ($)</label>
                        <input
                          type="number"
                          min={0}
                          value={approvals.overrides.auto_place_orders.conditions.max_order_total ?? ''}
                          onChange={(e) => updateOverride('auto_place_orders', {
                            ...approvals.overrides.auto_place_orders,
                            conditions: { ...approvals.overrides.auto_place_orders.conditions, max_order_total: e.target.value ? Number(e.target.value) : null },
                          })}
                          placeholder="No limit"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-500"
                        />
                      </div>
                      <label className="flex items-center gap-2 self-end pb-1">
                        <input
                          type="checkbox"
                          checked={approvals.overrides.auto_place_orders.conditions.trusted_suppliers_only}
                          onChange={(e) => updateOverride('auto_place_orders', {
                            ...approvals.overrides.auto_place_orders,
                            conditions: { ...approvals.overrides.auto_place_orders.conditions, trusted_suppliers_only: e.target.checked },
                          })}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-xs text-gray-600">Trusted (active) suppliers only</span>
                      </label>
                    </div>
                  </OverrideToggle>

                  {/* Auto-promote prospects */}
                  <OverrideToggle
                    label="Auto-promote prospects"
                    description="Automatically promote researched prospects to approved status"
                    enabled={approvals.overrides.auto_promote_prospects.enabled}
                    onToggle={(v) => updateOverride('auto_promote_prospects', { enabled: v })}
                  />

                  {/* Auto-chain research */}
                  <OverrideToggle
                    label="Auto-chain research"
                    description="Automatically start deep research after supplier discovery"
                    enabled={approvals.overrides.auto_chain_research.enabled}
                    onToggle={(v) => updateOverride('auto_chain_research', { enabled: v })}
                  />

                  {/* Auto-send supplier emails */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={approvals.overrides.auto_send_supplier_emails?.enabled ?? false}
                      onChange={() => setApprovals((a) => ({
                        ...a,
                        overrides: {
                          ...a.overrides,
                          auto_send_supplier_emails: {
                            ...a.overrides.auto_send_supplier_emails,
                            enabled: !a.overrides.auto_send_supplier_emails?.enabled,
                          },
                        },
                      }))}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Auto-send supplier emails</span>
                  </label>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ Tab: Technical ═══ */}
      {activeTab === 'technical' && (
        <div className="space-y-6">
          {/* Model Routing */}
          <Card title="Model Routing" padding="none">
            <p className="px-5 pb-3 text-sm text-gray-500 flex items-center gap-1.5">
              Assign AI model tiers to each task type. Changes take effect on the next task execution.
              <Tooltip content="Cheap: DeepSeek/Pollinations (~$0.001/req). Standard: Google Gemini (~$0.01/req). Powerful: Anthropic/Google (~$0.05/req)." />
            </p>
            {routingRows.length === 0 ? (
              <div className="px-5 pb-5 text-sm text-gray-500">No routing rules configured.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-t border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model Override</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {routingRows.map((row, i) => (
                      <tr key={row.task_type} className="hover:bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-800">{row.task_type}</span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <select
                            value={row.tier}
                            onChange={(e) => updateRoutingTier(i, e.target.value)}
                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            {TIER_OPTIONS.map((t) => (
                              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_COLORS[row.tier] ?? 'bg-gray-100 text-gray-700'}`}>
                            {row.tier}
                          </span>
                          {row.model && <span className="ml-2 text-xs text-gray-400 font-mono">{row.model}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Pricing Rules */}
          <Card title="Pricing Rules" padding="md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="Min Margin %" type="number" value={pricing.min_margin_percent}
                onChange={(v) => setPricing((p) => ({ ...p, min_margin_percent: Number(v) }))}
                hint="Minimum acceptable profit margin before alerting"
                tooltip="Orders below this margin trigger a price_alert notification to the admin" />
              <FormField label="Default Markup %" type="number" value={pricing.default_markup_percent}
                onChange={(v) => setPricing((p) => ({ ...p, default_markup_percent: Number(v) }))}
                hint="Applied to supplier cost when listing price is absent" />
              <FormField label="Max Markup %" type="number" value={pricing.max_markup_percent}
                onChange={(v) => setPricing((p) => ({ ...p, max_markup_percent: Number(v) }))}
                hint="Cap on markup to stay competitive" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Apply Pricing</label>
                <div className="flex items-center gap-2 mt-2">
                  <input id="auto_apply" type="checkbox" checked={pricing.auto_apply}
                    onChange={(e) => setPricing((p) => ({ ...p, auto_apply: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                  <label htmlFor="auto_apply" className="text-sm text-gray-700">
                    Automatically apply pricing rules without manual confirmation
                  </label>
                </div>
              </div>
            </div>
          </Card>

          {/* Escalation Rules */}
          <Card title="Escalation Rules" padding="md">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <FormField label="Max Retries" type="number" value={escalation.max_retries}
                onChange={(v) => setEscalation((e) => ({ ...e, max_retries: Number(v) }))}
                hint="Retry attempts before escalating to a human"
                tooltip="Number of times the agent retries a failed task before escalating to you via notification" />
              <FormField label="Price Spike Threshold %" type="number" value={escalation.price_spike_threshold_percent}
                onChange={(v) => setEscalation((e) => ({ ...e, price_spike_threshold_percent: Number(v) }))}
                hint="Alert when supplier price increases by this percentage" />
              <FormField label="Supplier Timeout (hours)" type="number" value={escalation.supplier_response_timeout_hours}
                onChange={(v) => setEscalation((e) => ({ ...e, supplier_response_timeout_hours: Number(v) }))}
                hint="Hours to wait for supplier confirmation before escalating" />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface FormFieldProps {
  label: string;
  type: 'text' | 'number';
  value: string | number;
  onChange: (v: string) => void;
  hint?: string;
  tooltip?: string;
}

function FormField({ label, type, value, onChange, hint, tooltip }: FormFieldProps) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
        {label}
        {tooltip && <Tooltip content={tooltip} />}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

interface OverrideToggleProps {
  label: string;
  description: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children?: React.ReactNode;
}

function OverrideToggle({ label, description, enabled, onToggle, children }: OverrideToggleProps) {
  return (
    <div className={`rounded-lg border p-3 transition-colors ${enabled ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-800">{label}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
        <button
          role="switch"
          aria-checked={enabled}
          onClick={() => onToggle(!enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-green-600' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      {enabled && children}
    </div>
  );
}

function RegionInput({ onAdd }: { onAdd: (region: string) => void }) {
  const [value, setValue] = useState('');
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value.toUpperCase())}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault();
          onAdd(value);
          setValue('');
        }
      }}
      placeholder="Type region code + Enter (e.g. US, CA, EU)"
      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  );
}
